import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import AppShell from "../components/layout/AppShell";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Input from "../components/ui/Input";
import Icon from "../components/ui/Icon";
import { Parse } from "../lib/parse";
import { MAX_DOCUMENT_FILE_SIZE_MB } from "../config/uploadLimits";

const TEMPLATE_CLASS = "contracts_Template";
const EXTENDED_USER_CLASS = "contracts_Users";
const TEMPLATE_REPORT_ID = "6TeaPr321t";
const DEFAULT_MIN_SIGNATURE_AREA = 1500;
const DEFAULT_MIN_SIGNATURE_LENGTH = 180;
const SUPPORTED_IMAGE_TYPES = ["image/png", "image/jpeg", "image/jpg"];

const parseStoredJson = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
};

const readFileAsArrayBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event?.target?.result || null);
    reader.onerror = () => reject(reader.error || new Error("File read error"));
    reader.readAsArrayBuffer(file);
  });

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(String(event?.target?.result || ""));
    reader.onerror = () => reject(reader.error || new Error("File read error"));
    reader.readAsDataURL(file);
  });

const buildPdfFileName = (fileName = "") => {
  const cleanedName = String(fileName || "")
    .trim()
    .replace(/\.[^/.]+$/, "")
    .trim();
  const safeName = (cleanedName || "document")
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  const normalizedSafeName = safeName || `document_${Date.now()}`;
  return `${normalizedSafeName}.pdf`;
};

const convertImageToPdfBytes = async (file) => {
  const { PDFDocument } = await import("pdf-lib");
  const dataUrl = await readFileAsDataUrl(file);
  const pdfDoc = await PDFDocument.create();
  const imageType = String(file?.type || "").toLowerCase();
  const image =
    imageType === "image/png" ? await pdfDoc.embedPng(dataUrl) : await pdfDoc.embedJpg(dataUrl);
  const page = pdfDoc.addPage([image.width, image.height]);
  page.drawImage(image, {
    x: 0,
    y: 0,
    width: image.width,
    height: image.height
  });
  return pdfDoc.save({ useObjectStreams: false });
};

const generateTitleFromFilename = (filename) => {
  try {
    let title = String(filename || "").trim();
    const lastDotIndex = title.lastIndexOf(".");
    if (lastDotIndex > 0) {
      title = title.slice(0, lastDotIndex);
    }
    title = title.replace(/[^\p{L}\p{N}\s-]/gu, " ");
    title = title.replace(/\s+/g, " ");
    title = title.replace(/\p{L}\S*/gu, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
    if (title.length > 100) {
      title = title.slice(0, 100).trim();
    }
    return title || "Untitled Template";
  } catch (error) {
    return "Untitled Template";
  }
};

function TemplateCreatePage() {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const extUser = useMemo(() => {
    const extClass = parseStoredJson("Extand_Class");
    return Array.isArray(extClass) ? extClass?.[0] || {} : {};
  }, []);
  const canManageTemplates = String(extUser?.UserRole || "").trim() !== "contracts_User";
  const navigate = useNavigate();

  useEffect(() => {
    if (!canManageTemplates) {
      navigate("/report/6TeaPr321t", { replace: true });
    }
  }, [canManageTemplates, navigate]);

  const defaultSendInOrder = extUser?.SendinOrder !== undefined ? Boolean(extUser.SendinOrder) : true;
  const defaultIsTourEnabled = extUser?.IsTourEnabled !== undefined ? Boolean(extUser.IsTourEnabled) : true;
  const defaultIsEnableOTP = extUser?.IsEnableOTP !== undefined ? Boolean(extUser.IsEnableOTP) : false;
  const defaultNotifyOnSignatures =
    extUser?.NotifyOnSignatures !== undefined ? Boolean(extUser.NotifyOnSignatures) : true;
  const defaultTimeToCompleteDays = (() => {
    const parsed = Number.parseInt(extUser?.DefaultTimeToCompleteDays, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 15;
  })();

  const [fileName, setFileName] = useState("");
  const [uploadedFileUrl, setUploadedFileUrl] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [formState, setFormState] = useState({
    title: "",
    description: "",
    note: t("request-sign-note", { defaultValue: "Por favor revisa y firma este documento." }),
    timeToCompleteDays: defaultTimeToCompleteDays,
    automaticReminders: false,
    remindOnceInEvery: 5,
    sendInOrder: defaultSendInOrder,
    isEnableOTP: defaultIsEnableOTP,
    isTourEnabled: defaultIsTourEnabled,
    notifyOnSignatures: defaultNotifyOnSignatures
  });

  const triggerFilePicker = () => {
    if (!canManageTemplates) {
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const sizeInMb = file.size / (1024 * 1024);
    if (sizeInMb > MAX_DOCUMENT_FILE_SIZE_MB) {
      setError(
        t("new-ui.upload.max-size-error", {
          defaultValue: "File exceeds maximum size of {{size}} MB.",
          size: MAX_DOCUMENT_FILE_SIZE_MB
        })
      );
      event.target.value = "";
      return;
    }

    const fileType = String(file.type || "").toLowerCase();
    const isPdf = fileType === "application/pdf";
    const isSupportedImage = SUPPORTED_IMAGE_TYPES.includes(fileType);
    if (!isPdf && !isSupportedImage) {
      setError(t("file-alert-2", { defaultValue: "Please upload PDF, PNG, JPG, or JPEG files only." }));
      event.target.value = "";
      return;
    }

    setError("");
    setIsUploading(true);
    setUploadProgress(0);

    try {
      const rawBytes = isPdf ? await readFileAsArrayBuffer(file) : await convertImageToPdfBytes(file);
      const fileBytes = new Uint8Array(rawBytes);
      const parseFileName = buildPdfFileName(file.name);
      const parseFile = new Parse.File(parseFileName, [...fileBytes], "application/pdf");
      const fileResult = await parseFile.save({
        progress: (progressValue, loaded, total, context = {}) => {
          if (context.type === "upload" && typeof loaded === "number" && typeof total === "number" && total > 0) {
            setUploadProgress(Math.max(0, Math.min(100, Math.round((loaded * 100) / total))));
            return;
          }
          if (typeof progressValue === "number") {
            setUploadProgress(Math.max(0, Math.min(100, Math.round(progressValue * 100))));
          }
        }
      });

      const sourceUrl = fileResult?.url?.() || parseFile.url();
      if (!sourceUrl) {
        throw new Error(
          t("new-ui.sign-yourself.upload-failed", {
            defaultValue: "Could not upload file."
          })
        );
      }

      setUploadedFileUrl(sourceUrl);
      setFileName(isSupportedImage ? `${file.name} (PDF)` : file.name);
      setFormState((current) => ({
        ...current,
        title: current.title || generateTitleFromFilename(file.name)
      }));
    } catch (uploadError) {
      console.error("Template upload failed", uploadError);
      setError(
        uploadError?.message ||
          t("new-ui.sign-yourself.upload-failed", {
            defaultValue: "Could not upload file."
          })
      );
      if (event.target) {
        event.target.value = "";
      }
    } finally {
      setIsUploading(false);
    }
  };

  const clearForm = () => {
    setFileName("");
    setUploadedFileUrl("");
    setUploadProgress(0);
    setError("");
    setFormState({
      title: "",
      description: "",
      note: t("request-sign-note", { defaultValue: "Por favor revisa y firma este documento." }),
      timeToCompleteDays: defaultTimeToCompleteDays,
      automaticReminders: false,
      remindOnceInEvery: 5,
      sendInOrder: defaultSendInOrder,
      isEnableOTP: defaultIsEnableOTP,
      isTourEnabled: defaultIsTourEnabled,
      notifyOnSignatures: defaultNotifyOnSignatures
    });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const openTemplateEditor = (templateId) => {
    if (!templateId) {
      return;
    }
    navigate(`/report/${TEMPLATE_REPORT_ID}`, {
      state: { autoEditTemplateId: templateId }
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!canManageTemplates) {
      setError(
        t("something-went-wrong-mssg", {
          defaultValue: "Something went wrong."
        })
      );
      return;
    }
    setError("");

    if (!uploadedFileUrl) {
      setError(t("file-alert-3", { defaultValue: "Please wait while the document uploads." }));
      return;
    }

    const trimmedTitle = String(formState.title || "").trim();
    if (!trimmedTitle) {
      setError(
        t("new-ui.sign-yourself.title-required", {
          defaultValue: "Document title is required."
        })
      );
      return;
    }

    const timeToCompleteDays = Number.parseInt(formState.timeToCompleteDays, 10);
    const remindOnceInEvery = Number.parseInt(formState.remindOnceInEvery, 10);
    if (!Number.isFinite(timeToCompleteDays) || timeToCompleteDays <= 0) {
      setError(t("input-required", { defaultValue: "Please fill out this field." }));
      return;
    }
    if (formState.automaticReminders) {
      if (!Number.isFinite(remindOnceInEvery) || remindOnceInEvery <= 0) {
        setError(t("input-required", { defaultValue: "Please fill out this field." }));
        return;
      }
      const reminderCount = timeToCompleteDays / remindOnceInEvery;
      if (reminderCount > 15) {
        setError(
          t("only-15-reminder-allowed", {
            defaultValue:
              "You can set up to 15 automatic reminders. For example, if 'TimeToComplete' is 15 days and 'RemindOnceInEvery' is 1 day, you'll reach the maximum limit of 15 reminders. Adjust your settings accordingly."
          })
        );
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const currentUser = Parse.User.current();
      if (!currentUser?.id) {
        throw new Error(t("invalid-session", { defaultValue: "Invalid session." }));
      }

      const templateObject = new Parse.Object(TEMPLATE_CLASS);
      templateObject.set("Name", trimmedTitle);
      templateObject.set("Description", String(formState.description || "").trim());
      templateObject.set("Note", String(formState.note || "").trim());
      templateObject.set("URL", uploadedFileUrl);
      templateObject.set("CreatedBy", Parse.User.createWithoutData(currentUser.id));
      templateObject.set("SendinOrder", Boolean(formState.sendInOrder));
      templateObject.set("AutomaticReminders", Boolean(formState.automaticReminders));
      templateObject.set("RemindOnceInEvery", Number.isFinite(remindOnceInEvery) ? remindOnceInEvery : 5);
      templateObject.set("TimeToCompleteDays", timeToCompleteDays);
      templateObject.set("AllowModifications", false);
      templateObject.set("IsEnableOTP", Boolean(formState.isEnableOTP));
      templateObject.set("IsTourEnabled", Boolean(formState.isTourEnabled));
      templateObject.set("NotifyOnSignatures", Boolean(formState.notifyOnSignatures));
      const tenantMinSignatureArea = Number.parseInt(extUser?.TenantId?.MinSignatureArea, 10);
      const tenantMinSignatureLength = Number.parseInt(extUser?.TenantId?.MinSignatureLength, 10);
      templateObject.set(
        "MinSignatureArea",
        tenantMinSignatureArea || Number.parseInt(extUser?.MinSignatureArea, 10) || DEFAULT_MIN_SIGNATURE_AREA
      );
      templateObject.set(
        "MinSignatureLength",
        tenantMinSignatureLength ||
          Number.parseInt(extUser?.MinSignatureLength, 10) ||
          DEFAULT_MIN_SIGNATURE_LENGTH
      );

      if (extUser?.objectId) {
        templateObject.set("ExtUserPtr", {
          __type: "Pointer",
          className: EXTENDED_USER_CLASS,
          objectId: extUser.objectId
        });
      }
      if (extUser?.TenantId?.ActiveFileAdapter) {
        templateObject.set("FileAdapterId", extUser.TenantId.ActiveFileAdapter);
      }

      const savedTemplate = await templateObject.save();
      openTemplateEditor(savedTemplate?.id);
    } catch (saveError) {
      console.error("Failed to create template", saveError);
      setError(
        saveError?.message ||
          t("something-went-wrong-mssg", {
            defaultValue: "Something went wrong."
          })
      );
      setIsSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="dana-section-grid">
        <Card className="animate-fade-up">
          <div className="dana-section-head">
            <span className="dana-section-icon">
              <Icon name="templateDoc" />
            </span>
            <div>
              <p className="ui-kicker">{t("new-ui.sign-yourself.kicker", { defaultValue: "Create template" })}</p>
              <h1 className="ui-title dana-section-title">
                {t("sidebar.Templates-Children.Create template", { defaultValue: "Create template" })}
              </h1>
            </div>
          </div>
          <p className="ui-copy mt-3">
            {t("template-form-description", {
              defaultValue: "Use this form to create reusable templates for your documents."
            })}
          </p>
        </Card>

        <Card className="animate-fade-up [animation-delay:40ms]">
          <form className="grid grid-cols-1 gap-3" onSubmit={handleSubmit}>
            <div>
              <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted">
                {`${t("report-heading.File", { defaultValue: "File" })} (${t("file-type", {
                  defaultValue: "pdf, png, jpg, jpeg"
                })})`}
              </label>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={handleFileChange}
                accept="application/pdf,image/png,image/jpeg,image/jpg"
              />
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  onClick={triggerFilePicker}
                  disabled={isUploading || isSubmitting}
                  className="!border !border-[#1ea34f] !bg-[#22c55e] !text-white hover:!bg-[#1ea34f] focus-visible:!ring-[rgba(34,197,94,0.35)]"
                >
                  <span className="inline-flex items-center gap-2">
                    <Icon name="templateDoc" />
                    {t("new-ui.sign-yourself.choose-file", { defaultValue: "Cargar archivo" })}
                  </span>
                </Button>
                {fileName ? (
                  <p className="m-0 truncate text-xs text-app-ink-muted" title={fileName}>
                    {t("file-selected", { defaultValue: "Selected file" })}: {fileName}
                  </p>
                ) : (
                  <p className="m-0 text-xs text-app-ink-muted">
                    {t("new-ui.sign-yourself.no-file", { defaultValue: "No file selected." })}
                  </p>
                )}
              </div>
              <p className="m-0 mt-1 text-[11px] text-app-ink-muted">
                {t("new-ui.upload.max-size", {
                  defaultValue: "Maximum file size: {{size}} MB",
                  size: MAX_DOCUMENT_FILE_SIZE_MB
                })}
              </p>
              {isUploading ? (
                <div className="mt-2">
                  <div className="h-1.5 w-full rounded-full bg-slate-200">
                    <div className="h-1.5 rounded-full bg-app-brand transition-all" style={{ width: `${uploadProgress}%` }} />
                  </div>
                  <p className="m-0 mt-1 text-[11px] text-app-ink-muted">{uploadProgress}%</p>
                </div>
              ) : null}
            </div>

            <Input
              id="template-title"
              label={t("template-title", { defaultValue: "Template title" })}
              value={formState.title}
              onChange={(event) => setFormState((current) => ({ ...current, title: event.target.value }))}
              required
            />

            <Input
              id="template-description"
              label={t("description", { defaultValue: "Description" })}
              value={formState.description}
              onChange={(event) => setFormState((current) => ({ ...current, description: event.target.value }))}
            />

            <div className="w-full">
              <label
                htmlFor="template-note"
                className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.08em] text-app-ink-muted"
              >
                {t("report-heading.Note", { defaultValue: "Note" })}
              </label>
              <textarea
                id="template-note"
                className="min-h-[86px] w-full rounded-xl border border-app-line bg-white px-3 py-2.5 text-sm text-app-ink shadow-soft outline-none transition focus:border-app-brand focus:ring-4 focus:ring-app-brand/15"
                value={formState.note}
                onChange={(event) => setFormState((current) => ({ ...current, note: event.target.value }))}
              />
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                id="template-time-to-complete"
                type="number"
                min="1"
                label={t("time-to-complete", { defaultValue: "Time to complete (days)" })}
                value={String(formState.timeToCompleteDays)}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, timeToCompleteDays: Number.parseInt(event.target.value, 10) || 0 }))
                }
                required
              />
              <Input
                id="template-remind-interval"
                type="number"
                min="1"
                label={t("remind-once-in", { defaultValue: "Remind once in (days)" })}
                value={String(formState.remindOnceInEvery)}
                onChange={(event) =>
                  setFormState((current) => ({ ...current, remindOnceInEvery: Number.parseInt(event.target.value, 10) || 0 }))
                }
                disabled={!formState.automaticReminders}
              />
            </div>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label className="inline-flex items-center gap-2 text-xs text-app-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-app-line text-app-brand focus:ring-app-brand/30"
                  checked={formState.sendInOrder}
                  onChange={(event) => setFormState((current) => ({ ...current, sendInOrder: event.target.checked }))}
                />
                <span>{t("send-in-order", { defaultValue: "Send in order" })}</span>
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-app-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-app-line text-app-brand focus:ring-app-brand/30"
                  checked={formState.automaticReminders}
                  onChange={(event) =>
                    setFormState((current) => ({ ...current, automaticReminders: event.target.checked }))
                  }
                />
                <span>{t("automatic-reminders", { defaultValue: "Automatic reminders" })}</span>
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-app-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-app-line text-app-brand focus:ring-app-brand/30"
                  checked={formState.isEnableOTP}
                  onChange={(event) => setFormState((current) => ({ ...current, isEnableOTP: event.target.checked }))}
                />
                <span>{t("otp", { defaultValue: "OTP verification" })}</span>
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-app-ink">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-app-line text-app-brand focus:ring-app-brand/30"
                  checked={formState.isTourEnabled}
                  onChange={(event) => setFormState((current) => ({ ...current, isTourEnabled: event.target.checked }))}
                />
                <span>{t("new-ui.request-sign.is-tour-enabled", { defaultValue: "Enable signing guide tour" })}</span>
              </label>
              <label className="inline-flex items-center gap-2 text-xs text-app-ink sm:col-span-2">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-app-line text-app-brand focus:ring-app-brand/30"
                  checked={formState.notifyOnSignatures}
                  onChange={(event) =>
                    setFormState((current) => ({ ...current, notifyOnSignatures: event.target.checked }))
                  }
                />
                <span>{t("notify-on-signatures", { defaultValue: "Notify on signatures" })}</span>
              </label>
            </div>

            {error ? <p className="m-0 text-sm text-app-danger">{error}</p> : null}

            <div className="mt-1 flex flex-wrap gap-2">
              <Button type="submit" disabled={isUploading || isSubmitting || !uploadedFileUrl}>
                {isSubmitting ? t("loading", { defaultValue: "Loading..." }) : t("create-template", { defaultValue: "Create template" })}
              </Button>
              <Button type="button" variant="ghost" onClick={clearForm} disabled={isSubmitting}>
                {t("reset", { defaultValue: "Reset" })}
              </Button>
            </div>
          </form>
        </Card>

      </div>
    </AppShell>
  );
}

export default TemplateCreatePage;
